import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useStaticCallback from 'hooks/useStaticCallback';
import { CreationStatus } from 'redux/middleware/api/creation-service/types';
import {
  cancelWaitForCreation,
  waitForCreation,
} from 'redux/modules/wizard-export/actions';
import {
  creationIdSelector,
  creationStatusExtractor,
  creationStatusSelector,
} from 'redux/modules/wizard-export/selectors';
import { Dispatch } from 'redux/types';
import { FatalError } from 'utils/FatalError';
import { ClipSelectionMode } from '../ClipVideoStep';
import WizardProgressStep, {
  WizardSubmitProgress,
} from '../WizardProgressStep';
import { VideoWizardObjective } from './types';

interface Props {
  clipSelectionMode: ClipSelectionMode;
  isFullEpisode: boolean;
  isSingleClip: boolean;
  objective: VideoWizardObjective;
  onCompleted: (creationId: number) => void;
  onVideoExportStart?: () => void;
  onError?: (e: Error) => void;
}

export const VideoWizardProgressStep: React.FC<Props> = props => {
  const {
    clipSelectionMode,
    isFullEpisode,
    isSingleClip,
    objective,
    onCompleted,
    onVideoExportStart,
    onError,
  } = props;
  const dispatch = useDispatch<Dispatch>();
  const creationId = useSelector(creationIdSelector);
  const creationStatus = useSelector(creationStatusSelector);
  const handleError = useStaticCallback(onError);

  useEffect(() => {
    const waitForExport = async () => {
      try {
        await dispatch(
          waitForCreation(creation =>
            [CreationStatus.COMPLETED, CreationStatus.EXPORTING_VIDEO].includes(
              creationStatusExtractor(creation),
            ),
          ),
        );
      } catch {
        handleError?.(new FatalError('Error creation video'));
      }
    };

    // hook should effectively only run when creationId changes, so this conditional
    // should be executed only once
    if (creationId) {
      waitForExport();
    }

    return () => {
      dispatch(cancelWaitForCreation());
    };
  }, [creationId, dispatch, handleError]);

  const completedSteps: WizardSubmitProgress[] = useMemo(() => {
    const steps = [];

    if (
      [
        ...(!isFullEpisode ? [CreationStatus.CREATING_CONFIG] : []),
        CreationStatus.COMPLETED,
        CreationStatus.EXPORTING_VIDEO,
      ].includes(creationStatus)
    ) {
      steps.push(WizardSubmitProgress.VIDEO_UPLOADING);
    }

    if (
      [CreationStatus.COMPLETED, CreationStatus.EXPORTING_VIDEO].includes(
        creationStatus,
      )
    ) {
      steps.push(WizardSubmitProgress.PROJECT_CREATING);
    }

    return steps;
  }, [creationStatus, isFullEpisode]);

  const handleCompleted = useCallback((): void => {
    if (isSingleClip) {
      if (clipSelectionMode === 'clip') {
        onCompleted(creationId);
      } else {
        onVideoExportStart();
      }
    }
  }, [
    clipSelectionMode,
    creationId,
    isSingleClip,
    onCompleted,
    onVideoExportStart,
  ]);

  useEffect(() => {
    if (
      creationStatus === CreationStatus.EXPORTING_VIDEO ||
      (objective === 'YouTubeMostReplayed' &&
        creationStatus === CreationStatus.SUBMITTED)
    ) {
      onVideoExportStart();
    }
  }, [creationStatus, objective, onVideoExportStart]);

  return (
    <WizardProgressStep
      steps={[
        WizardSubmitProgress.VIDEO_UPLOADING,
        ...(clipSelectionMode === 'clip' && isSingleClip
          ? [WizardSubmitProgress.PROJECT_CREATING]
          : []),
      ]}
      completedSteps={completedSteps}
      onCompleted={handleCompleted}
    />
  );
};

export default VideoWizardProgressStep;

export { Props as VideoWizardProgressStepProps };
